import { API_BASE } from './constants'
import type {
  ApiDataResponse,
  ApiListResponse,
  AuthProviders,
  Candidato,
  CandidaturaPublica,
  EmpresaPublica,
  VagaDetalhe,
  VagaResumo,
} from './types'

const CLIENT_HEADER = 'hi-carreiras-public'

function apiError(json: { mensagem?: string; detail?: string }, fallback: string) {
  return new Error(json.mensagem || json.detail || fallback)
}

export async function apiGet<T = unknown>(
  path: string,
  { auth = false, token = '' }: { auth?: boolean; token?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-App-Client': CLIENT_HEADER,
  }
  if (auth && token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, { headers })
  const json = (await res.json().catch(() => ({}))) as T & {
    mensagem?: string
    detail?: string
  }

  if (!res.ok) {
    const err = apiError(json, 'Falha na requisição.') as Error & { status?: number }
    err.status = res.status
    throw err
  }
  return json
}

export async function apiPostJson<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-App-Client': CLIENT_HEADER,
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as T & {
    mensagem?: string
    detail?: string
  }
  if (!res.ok) throw apiError(json, 'Falha na autenticação.')
  return json
}

export async function apiPostForm<T = unknown>(
  path: string,
  formData: FormData,
  token = '',
): Promise<T> {
  const headers: Record<string, string> = { 'X-App-Client': CLIENT_HEADER }
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  })
  const json = (await res.json().catch(() => ({}))) as T & {
    mensagem?: string
    detail?: string
  }
  if (!res.ok) {
    const err = apiError(json, 'Falha ao enviar candidatura.') as Error & { status?: number }
    err.status = res.status
    throw err
  }
  return json
}

export async function fetchEmpresaPublica() {
  return apiGet<ApiDataResponse<EmpresaPublica> & EmpresaPublica>('/ats/publico/empresa/')
}

export async function fetchAuthProviders() {
  return apiGet<ApiDataResponse<AuthProviders>>('/ats/publico/auth/providers/')
}

export async function fetchVagas(params: { q?: string; onde?: string; empresa?: string }) {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.onde) qs.set('onde', params.onde)
  if (params.empresa) qs.set('empresa', params.empresa)
  qs.set('por_pagina', '50')
  return apiGet<ApiListResponse<VagaResumo>>(`/ats/publico/vagas/?${qs}`)
}

export async function fetchVaga(id: string) {
  return apiGet<ApiDataResponse<VagaDetalhe> & VagaDetalhe>(`/ats/publico/vagas/${id}/`)
}

export async function fetchMe(token: string) {
  return apiGet<ApiDataResponse<Candidato> & { candidato?: Candidato }>(
    '/ats/publico/auth/me/',
    { auth: true, token },
  )
}

export async function fetchMinhasCandidaturas(token: string) {
  return apiGet<ApiListResponse<CandidaturaPublica>>(
    '/ats/publico/auth/minhas-candidaturas/',
    { auth: true, token },
  )
}

export async function loginCandidato(email: string, senha: string) {
  return apiPostJson<ApiDataResponse<Candidato> & { access?: string; candidato?: Candidato }>(
    '/ats/publico/auth/login/',
    { email, senha },
  )
}

export async function registroCandidato(nome: string, email: string, senha: string) {
  return apiPostJson<ApiDataResponse<Candidato> & { access?: string; candidato?: Candidato }>(
    '/ats/publico/auth/registro/',
    { nome, email, senha },
  )
}

export async function fetchOAuthUrl(provider: 'google' | 'linkedin', vagaId?: string) {
  const qs = vagaId ? `?vaga=${encodeURIComponent(vagaId)}` : ''
  const path =
    provider === 'linkedin'
      ? `/ats/publico/auth/linkedin/${qs}`
      : `/ats/publico/auth/google/${qs}`
  return apiGet<ApiDataResponse<never> & { authorize_url?: string }>(path)
}

export async function enviarCandidatura(vagaId: string, formData: FormData, token: string) {
  return apiPostForm<ApiDataResponse<never>>(
    `/ats/publico/vagas/${vagaId}/candidatar/`,
    formData,
    token,
  )
}
